import React, { useMemo } from "react";

import {
  ContentContainer,
  DefaultMarker,
  HorizontalConnector,
  HorizontalContainer,
  HorizontalContentContainer,
  HorizontalItemContainer,
  HorizontalRail,
  MarkerInnerDot,
  TIMELINE_COLORS,
  VerticalConnector,
  VerticalContainer,
  VerticalItemContainer,
  VerticalRail,
} from "./Timeline.style";
import type {
  TimelineColorTokens,
  TimelineItem,
  TimelineProps,
  TimelineState,
} from "./types";

const DEFAULT_COLORS: TimelineColorTokens = {
  completedColor: TIMELINE_COLORS.completed,
  inProgressColor: TIMELINE_COLORS.inProgress,
  pendingColor: TIMELINE_COLORS.pending,
  errorColor: TIMELINE_COLORS.error,
};

const getColor = (
  state: TimelineState,
  colors: TimelineColorTokens
): string => {
  switch (state) {
    case "completed":
      return colors.completedColor;
    case "in_progress":
      return colors.inProgressColor;
    case "error":
      return colors.errorColor;
    case "pending":
    default:
      return colors.pendingColor;
  }
};

// ── Tick SVG ─────────────────────────────────────────────────────────────────

const TickIcon = ({ size }: { size: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 10 8"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M1 3.5L3.5 6.5L9 1"
      stroke="#fff"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ── Default circular marker ───────────────────────────────────────────────────

const Marker = ({
  state,
  colors,
  size,
}: {
  state: TimelineState;
  colors: TimelineColorTokens;
  size: number;
}) => {
  const color = getColor(state, colors);
  const isPending = state === "pending";
  const isCompleted = state === "completed";
  const innerDotSize = Math.max(Math.round(size * 0.4), 6);
  const tickSize = Math.max(Math.round(size * 0.55), 9);

  return (
    <DefaultMarker
      size={size}
      borderColor={color}
      bgColor={isPending ? "#fff" : color}
    >
      {isCompleted ? (
        <TickIcon size={tickSize} />
      ) : !isPending ? (
        <MarkerInnerDot size={innerDotSize} />
      ) : null}
    </DefaultMarker>
  );
};

// ── Vertical timeline ─────────────────────────────────────────────────────────

const VerticalTimeline = ({
  items,
  colors,
  markerSize,
  connectorWidth,
  itemGap,
  contentGap,
}: Required<Omit<TimelineProps, "direction" | "className" | "colors">> & {
  colors: TimelineColorTokens;
}) => (
  <VerticalContainer>
    {items.map((item, index) => {
      const isLast = index === items.length - 1;
      const nextItem = items[index + 1];
      const connectorColor = nextItem
        ? getColor(nextItem.state, colors)
        : colors.pendingColor;

      return (
        <VerticalItemContainer key={item.id} isLast={isLast} itemGap={itemGap}>
          <VerticalRail markerSize={markerSize} contentGap={contentGap}>
            {item.marker ?? (
              <Marker state={item.state} colors={colors} size={markerSize} />
            )}
            {!isLast && (
              <VerticalConnector
                connectorWidth={connectorWidth}
                color={connectorColor}
                itemGap={itemGap}
              />
            )}
          </VerticalRail>
          <ContentContainer>{item.content}</ContentContainer>
        </VerticalItemContainer>
      );
    })}
  </VerticalContainer>
);

// ── Horizontal timeline ───────────────────────────────────────────────────────

const HorizontalTimeline = ({
  items,
  colors,
  markerSize,
  connectorWidth,
  itemGap,
}: Required<
  Omit<TimelineProps, "direction" | "className" | "colors" | "contentGap">
> & {
  colors: TimelineColorTokens;
}) => (
  <HorizontalContainer>
    {items.map((item, index) => {
      const isLast = index === items.length - 1;
      const nextItem = items[index + 1];
      const connectorColor = nextItem
        ? getColor(nextItem.state, colors)
        : colors.pendingColor;

      return (
        <HorizontalItemContainer
          key={item.id}
          isLast={isLast}
          itemGap={itemGap}
        >
          <HorizontalRail>
            {item.marker ?? (
              <Marker state={item.state} colors={colors} size={markerSize} />
            )}
            {!isLast && (
              <HorizontalConnector
                connectorWidth={connectorWidth}
                color={connectorColor}
              />
            )}
          </HorizontalRail>
          <HorizontalContentContainer>
            {item.content}
          </HorizontalContentContainer>
        </HorizontalItemContainer>
      );
    })}
  </HorizontalContainer>
);

// ── Public component ──────────────────────────────────────────────────────────

export const Timeline: React.FC<TimelineProps> = ({
  items,
  direction = "vertical",
  colors,
  markerSize = 20,
  connectorWidth = 2,
  itemGap = 16,
  contentGap = 20,
  className,
}) => {
  const visibleItems = useMemo(
    () => items.filter((item): item is TimelineItem => !item.hidden),
    [items]
  );

  const mergedColors = useMemo<TimelineColorTokens>(
    () => ({ ...DEFAULT_COLORS, ...colors }),
    [colors]
  );

  if (visibleItems.length === 0) return null;

  if (direction === "horizontal") {
    return (
      <div className={className}>
        <HorizontalTimeline
          items={visibleItems}
          colors={mergedColors}
          markerSize={markerSize}
          connectorWidth={connectorWidth}
          itemGap={itemGap}
        />
      </div>
    );
  }

  return (
    <div className={className}>
      <VerticalTimeline
        items={visibleItems}
        colors={mergedColors}
        markerSize={markerSize}
        connectorWidth={connectorWidth}
        itemGap={itemGap}
        contentGap={contentGap}
      />
    </div>
  );
};

export default Timeline;
